/**
 * Loans Page - Crypto-backed fiat loans with bank partner matching
 * 
 * States:
 * 1. No wallet - Marketing/SEO focused
 * 2. Wallet but insufficient collateral - Nudge to create vault/deposit
 * 3. Wallet with sufficient collateral - Loan request flow
 */

import { useState, Fragment } from 'react'
import { useAccount, useSignMessage } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { Dialog, Transition } from '@headlessui/react'
import { SEO, generateBreadcrumbSchema, financialProductSchema } from '../components/common/SEO'
import { Card, CardContent } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { InfoBox } from '../components/common/InfoBox'
import { HowItWorksFlow, StepProgress } from '../components/common/HowItWorksFlow'
import { useAllAccountCollateralBalances } from '../hooks/useVault'
import { useUserLOCsFromEvents, LOCEvent } from '../hooks/useLOCEvents'
import { useTokenPrices } from '../hooks/useTokenPrice'
import { useSubmitLoanRequest, useLoanRequests } from '../hooks/useLoanRequest'
// Note: Anvil uses account-based collateral, not vault IDs
import { formatTokenAmount as _formatTokenAmount } from '../utils/format'
import { formatUnits } from 'viem'
import { toast } from 'react-hot-toast'
import { SUPPORTED_TOKENS } from '@contracts/tokens'
import { 
  CurrencyIcon, 
  HomeIcon, 
  BusinessIcon,
  CheckCircleIcon 
} from '../assets/icons'
import {
  WalletConnectIllustration,
  CoinBooksIllustration,
  BlockSecureIllustration
} from '@bitcoin-com/react-components/src/components/mini-illustrations'
import { Accordion } from '@bitcoin-com/react-components/src/components/accordion'
import { KYCCompletionList } from '../components/loan/KYCCompletionList'
import type { KYCStep as _KYCStep } from '../components/loan/KYCCompletionList'
import { MINIMUM_COLLATERAL_USD, RECOMMENDED_LTV, MAX_LTV, DANGER_LTV } from '../config/constants'

// Bank Partner Addresses
const BANK_PARTNERS = {
  AMDAX: '0x717d52e84eF30875De52834603c112675CeDB7CA' as const,
  EMPOWERMINT: '0x765365f5DF83A79001229f4E76881976039DB8F5' as const,
} as const

interface LoanRequest {
  id: string
  userAddress: string
  requestedAmount: number
  ltv: number
  timestamp: number
  collateralValue: number
  signature: string
  vaultId: bigint
  status: 'pending' | 'accepted' | 'completed'
  acceptedBy?: keyof typeof BANK_PARTNERS
}

export function LoansPage() {
  const { address, isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()
  const { signMessageAsync } = useSignMessage()
  
  // Fetch user's collateral and LOC data (use event-based query since contract lacks getUserLOCs)
  const { data: collateralData, isLoading: isLoadingCollateral } = useAllAccountCollateralBalances(address)
  const { locs, isLoading: isLoadingLOCs } = useUserLOCsFromEvents(address)
  const { data: prices, isLoading: _isLoadingPrices } = useTokenPrices(Object.values(SUPPORTED_TOKENS))
  
  // Note: Anvil uses account-based collateral
  const hasAccount = !!address
  const vaultsLoading = isLoadingCollateral || isLoadingLOCs
  
  const [loanRequests, setLoanRequests] = useState<LoanRequest[]>([])
  const [showLoanRequestModal, setShowLoanRequestModal] = useState(false)
  const [ltvSlider, setLtvSlider] = useState(RECOMMENDED_LTV)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // LOC selection & bank partner pre-fill (3e)
  const [selectedLocEvent, setSelectedLocEvent] = useState<LOCEvent | undefined>()
  const [selectedBankPartner, setSelectedBankPartner] = useState<keyof typeof BANK_PARTNERS | ''>('')

  // Backend loan request persistence (3c)
  const { submit: submitToBackend } = useSubmitLoanRequest()
  const { requests: _backendRequests } = useLoanRequests(address)
  
  // Calculate total collateral value in USD
  let collateralValueUSD = 0
  if (collateralData && prices) {
    Object.entries(collateralData).forEach(([tokenAddress, { available }]) => {
      const token = Object.values(SUPPORTED_TOKENS).find(
        (t) => t.address.toLowerCase() === tokenAddress.toLowerCase()
      )
      if (token && available > 0n) {
        const balance = parseFloat(formatUnits(available, token.decimals))
        const price = prices[token.symbol] || 0
        collateralValueUSD += balance * price
      }
    })
  }
  const hasInsufficientCollateral = collateralValueUSD < MINIMUM_COLLATERAL_USD
  const maxLoanAmount = (collateralValueUSD * MAX_LTV) / 100
  
  // Calculate user progress through the flow
  const userProgress: StepProgress = {
    fundVault: isConnected && collateralValueUSD >= MINIMUM_COLLATERAL_USD, // Has deposited sufficient collateral
    issueLOC: isConnected && (locs?.length ?? 0) > 0, // Has issued at least one LOC
    applyLoan: isConnected && loanRequests.length > 0, // Has submitted loan application
    loanStatus: loanRequests.length > 0 
      ? loanRequests[0].status === 'pending' 
        ? 'pending' 
        : loanRequests[0].status === 'accepted'
        ? 'approved'
        : 'disbursed'
      : undefined
  }
  
  // Debug progress tracking
  console.log('🔍 [LoansPage] Progress Debug:', {
    isConnected,
    collateralValueUSD,
    MINIMUM_COLLATERAL_USD,
    fundVault: userProgress.fundVault,
    issueLOC: userProgress.issueLOC,
    locsLength: locs?.length ?? 0,
  })
  const requestedLoanAmount = (collateralValueUSD * ltvSlider) / 100
  
  // LTV slider color based on risk
  const getLtvColor = (ltv: number) => {
    if (ltv >= DANGER_LTV) return 'text-red-600'
    if (ltv === RECOMMENDED_LTV) return 'text-green-600'
    return 'text-yellow-600'
  }
  
  // getLtvBgColor removed (was unused)

  // Handle loan request submission
  const handleSubmitLoanRequest = async () => {
    if (!address || !hasAccount) return
    
    setIsSubmitting(true)
    try {
      // Create message to sign
      const message = JSON.stringify({
        action: 'REQUEST_LOAN',
        userAddress: address,
        collateralAccount: address, // Anvil uses account-based collateral
        requestedAmount: requestedLoanAmount.toFixed(2),
        ltv: ltvSlider,
        collateralValue: collateralValueUSD.toFixed(2),
        timestamp: Date.now(),
      })
      
      // Sign message
      const signature = await signMessageAsync({ message })
      
      // Create loan request
      const newRequest: LoanRequest = {
        id: `loan-${Date.now()}`,
        userAddress: address,
        requestedAmount: requestedLoanAmount,
        ltv: ltvSlider,
        timestamp: Date.now(),
        collateralValue: collateralValueUSD,
        signature,
        vaultId: 0n, // Not used in Anvil
        status: 'pending',
        acceptedBy: selectedBankPartner || undefined,
      }

      setLoanRequests(prev => [...prev, newRequest])

      // Also persist to backend + localStorage (3c)
      try {
        await submitToBackend({
          locId: selectedLocEvent ? selectedLocEvent.id.toString() : undefined,
          borrowerAddress: address,
          bankPartner: selectedBankPartner
            ? BANK_PARTNERS[selectedBankPartner as keyof typeof BANK_PARTNERS]
            : undefined,
          requestedAmount: requestedLoanAmount,
        })
      } catch (backendErr) {
        console.warn('[LoansPage] Backend persist failed (non-fatal):', backendErr)
      }

      toast.success('Loan request submitted! Bank partners will review your request.')
      setShowLoanRequestModal(false)
      setLtvSlider(RECOMMENDED_LTV)
      setSelectedLocEvent(undefined)
      setSelectedBankPartner('')
    } catch (error) {
      console.error('Error submitting loan request:', error)
      toast.error('Failed to submit loan request')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // Mock bank acceptance (in production, banks would do this)
  const mockBankAcceptance = (requestId: string, bank: keyof typeof BANK_PARTNERS) => {
    setLoanRequests(prev => prev.map(req => 
      req.id === requestId 
        ? { ...req, status: 'accepted', acceptedBy: bank }
        : req
    ))
    toast.success(`${bank} has accepted your loan request! Complete KYC to proceed.`)
  }

  const loansBreadcrumb = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Loans & Mortgages', url: '/loans' },
  ])

  const loansJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      financialProductSchema,
      loansBreadcrumb,
    ],
  }

  // STATE 1: No wallet connected - Marketing/SEO focused
  if (!isConnected) {
    return (
      <>
        <SEO
          title="Bitcoin-Backed Loans & Mortgages"
          description="Access EUR, USD, or GBP fiat liquidity without selling your Bitcoin. Self-custodial crypto-backed loans with competitive rates from partner banks."
          canonical="/loans"
          jsonLd={loansJsonLd}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="font-display font-black text-heading-2xl uppercase text-text-primary mb-m">
                Bitcoin-Backed Loans & Mortgages
              </h1>
              <p className="font-['Satoshi_Variable'] text-[18px] leading-normal text-text-secondary mb-l">
                Access fiat liquidity without selling your Bitcoin. Get EUR, USD, or GBP disbursed directly to your bank account while keeping full custody of your crypto assets.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" onClick={openConnectModal} className="w-full sm:w-auto min-h-[48px]">
                  Connect Wallet to Apply
                </Button>
                <Button size="lg" variant="default" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} className="w-full sm:w-auto min-h-[48px]">
                  Learn More
                </Button>
              </div>
            </div>

            {/* How It Works Flow */}
            <div id="how-it-works">
              <HowItWorksFlow 
                variant="full" 
                progress={userProgress}
                showProgress={isConnected}
              />
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              <Card>
                <CardContent className="text-center p-l">
                  <div className="flex justify-center mb-m">
                    <div className="w-20 h-20">
                      <BlockSecureIllustration className="w-full h-full" />
                    </div>
                  </div>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Self-Custodial</h3>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary">
                    Your Bitcoin stays in your vault. No rehypothecation, no counterparty risk.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="text-center p-l">
                  <div className="flex justify-center mb-m">
                    <div className="w-20 h-20">
                      <CoinBooksIllustration className="w-full h-full" />
                    </div>
                  </div>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Tax-Efficient</h3>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary">
                    Borrow instead of selling. Avoid capital gains taxes while accessing liquidity.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="text-center p-l">
                  <div className="flex justify-center mb-m">
                    <div className="w-20 h-20">
                      <CurrencyIcon />
                    </div>
                  </div>
                  <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Fiat Disbursement</h3>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary">
                    Get EUR, USD, or GBP sent directly to your IBAN bank account.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* How It Works */}
            <div id="how-it-works" className="scroll-mt-8">
              <h2 className="font-display font-black text-heading-xl uppercase text-text-primary text-center mb-l">
                How It Works
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { step: '1', title: 'Create Vault', desc: 'Set up your self-custodial smart contract vault' },
                  { step: '2', title: 'Deposit Crypto', desc: 'Add BTC, ETH, or other supported assets as collateral' },
                  { step: '3', title: 'Request Loan', desc: 'Choose your LTV and submit a signed loan request' },
                  { step: '4', title: 'Get Funded', desc: 'Complete KYC with partner bank and receive fiat' },
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-brand-orange text-white flex items-center justify-center text-xl sm:text-2xl font-bold mx-auto mb-m">
                      {item.step}
                    </div>
                    <h4 className="font-['Satoshi_Variable'] font-bold text-[14px] sm:text-[16px] leading-normal text-text-primary mb-xs">{item.title}</h4>
                    <p className="font-['Satoshi_Variable'] text-[12px] sm:text-[14px] leading-normal text-text-secondary">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Card className="bg-gradient-to-r from-brand-orange/10 to-brand-blue/10">
              <CardContent className="text-center py-xl">
                <h2 className="font-display font-black text-heading-lg uppercase text-text-primary mb-m">Ready to unlock your Bitcoin's value?</h2>
                <p className="font-['Satoshi_Variable'] text-[18px] leading-normal text-text-secondary mb-l max-w-2xl mx-auto">
                  Connect your wallet to see personalized loan offers based on your collateral.
                </p>
                <Button size="lg" onClick={openConnectModal}>
                  Connect Wallet Now
                </Button>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <FAQSection />
          </div>
        </div>
      </>
    )
  }

  // STATE 2: Wallet connected but insufficient collateral
  if (vaultsLoading) {
    return (
      <>
        <SEO
          title="Bitcoin-Backed Loans & Mortgages"
          description="Access EUR, USD, or GBP fiat liquidity without selling your Bitcoin. Self-custodial crypto-backed loans with competitive rates from partner banks."
          canonical="/loans"
          jsonLd={loansJsonLd}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center py-12">
            <div className="spinner h-12 w-12" />
          </div>
        </div>
      </>
    )
  }

  if (!hasAccount || hasInsufficientCollateral) {
    return (
      <>
        <SEO
          title="Bitcoin-Backed Loans & Mortgages"
          description="Access EUR, USD, or GBP fiat liquidity without selling your Bitcoin. Self-custodial crypto-backed loans with competitive rates from partner banks."
          canonical="/loans"
          jsonLd={loansJsonLd}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            <div>
              <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-2">
                Loans & Mortgages
              </h1>
              <p className="font-['Satoshi_Variable'] text-[16px] leading-normal text-text-secondary">
                Get fiat loans backed by your crypto collateral
              </p>
            </div>

            {/* How It Works Flow */}
            <HowItWorksFlow 
              variant="compact" 
              progress={userProgress}
              showProgress={true}
              clickable={true}
            />

            {/* Nudge Card */}
            <Card className="border-2 border-brand-orange">
              <CardContent className="p-4 sm:p-l">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-m">
                  <div className="w-16 h-16 flex-shrink-0 mx-auto sm:mx-0">
                    <WalletConnectIllustration className="w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-black text-heading-md uppercase text-text-primary mb-s text-center sm:text-left">
                      {!hasAccount ? 'Make a Deposit to Get Started' : 'Deposit More Collateral'}
                    </h3>
                    <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-m text-center sm:text-left">
                      {!hasAccount
                        ? 'You need to make a deposit of at least $500 worth of crypto to apply for loans.'
                        : `You need at least $${MINIMUM_COLLATERAL_USD} in collateral. Current collateral: $${collateralValueUSD.toFixed(2)}`
                      }
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button onClick={() => window.location.href = '/vaults'} className="w-full sm:w-auto min-h-[48px]">
                        {!hasAccount ? 'Make Deposit' : 'Deposit Collateral'}
                      </Button>
                      <Button variant="default" onClick={() => window.location.href = '/about'} className="w-full sm:w-auto min-h-[48px]">
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Greyed out loan types */}
            <div className="opacity-40 pointer-events-none">
              <h2 className="text-2xl font-bold mb-m">Loan Types</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="text-center py-l">
                    <div className="w-16 h-16 rounded-full bg-brand-orange/20 flex items-center justify-center mx-auto mb-m">
                      <CurrencyIcon />
                    </div>
                    <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Personal Loan</h3>
                    <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-m">€1K - €100K</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="text-center py-l">
                    <div className="w-16 h-16 rounded-full bg-brand-blue/20 flex items-center justify-center mx-auto mb-m">
                      <HomeIcon />
                    </div>
                    <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Mortgage</h3>
                    <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-m">€50K - €1M</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="text-center py-l">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-m">
                      <BusinessIcon />
                    </div>
                    <h3 className="font-display font-black text-heading-sm uppercase text-text-primary mb-s">Business Loan</h3>
                    <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-m">€10K - €500K</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <FAQSection />
          </div>
        </div>
      </>
    )
  }

  // STATE 3: Wallet with sufficient collateral - Full loan request flow
  return (
    <>
      <SEO
        title="Bitcoin-Backed Loans & Mortgages"
        description="Access EUR, USD, or GBP fiat liquidity without selling your Bitcoin. Self-custodial crypto-backed loans with competitive rates from partner banks."
        canonical="/loans"
        jsonLd={loansJsonLd}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div>
            <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-2">
              Loans & Mortgages
            </h1>
            <p className="font-['Satoshi_Variable'] text-[16px] leading-normal text-text-secondary">
              Request crypto-backed fiat loans from partner banks
            </p>
          </div>

          {/* How It Works Flow */}
          <HowItWorksFlow 
            variant="compact" 
            progress={userProgress}
            showProgress={true}
            clickable={true}
          />

          {/* Collateral Summary */}
          <Card>
            <CardContent className="p-l">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-xs">Your Collateral</p>
                  <p className="font-['IBMPlexSans'] text-[28px] sm:text-[32px] font-bold leading-normal text-text-primary">${collateralValueUSD.toFixed(2)}</p>
                </div>
                <div className="sm:text-right">
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-xs">Max Loan Amount</p>
                  <p className="font-['IBMPlexSans'] text-[28px] sm:text-[32px] font-bold leading-normal text-brand-orange">${maxLoanAmount.toFixed(2)}</p>
                  <p className="font-['Satoshi_Variable'] text-[12px] leading-normal text-text-secondary">at {MAX_LTV}% LTV</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Request Loan Button */}
          <div className="flex flex-col items-center gap-3">
            <Button 
              size="lg" 
              onClick={() => setShowLoanRequestModal(true)}
              disabled={!userProgress.issueLOC || hasInsufficientCollateral}
            >
              Request New Loan
            </Button>
            {!userProgress.issueLOC && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ You need to <a href="/locs" className="underline font-semibold">issue a Letter of Credit</a> before applying for a loan.
              </p>
            )}
            {hasInsufficientCollateral && userProgress.issueLOC && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                ⚠️ You need at least ${MINIMUM_COLLATERAL_USD} in collateral to request a loan.
              </p>
            )}
          </div>

          {/* Active Loan Requests */}
          {loanRequests.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-m">Your Loan Requests</h2>
              <div className="space-y-4">
                {loanRequests.map((request) => (
                  <Card key={request.id}>
                    <CardContent className="p-4 sm:p-l">
                      {/* Status Stepper (4c) */}
                      <LoanStatusStepper status={request.status as any} className="mb-m" />

                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-m gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-xs">
                            <h3 className="text-xl font-bold font-mono">${request.requestedAmount.toFixed(2)}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              request.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {request.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="font-['Satoshi_Variable'] text-[12px] sm:text-[14px] leading-normal text-text-secondary">
                            LTV: <span className={`font-semibold ${getLtvColor(request.ltv)}`}>{request.ltv}%</span>
                            {' • '}
                            Collateral: ${request.collateralValue.toFixed(2)}
                            {' • '}
                            {new Date(request.timestamp).toLocaleString()}
                          </p>
                        </div>
                        {request.status === 'accepted' && request.acceptedBy && (
                          <div className="mt-m pt-m border-t border-border-default">
                            <KYCCompletionList
                              bankName={request.acceptedBy}
                              steps={[
                                {
                                  id: 'kyc',
                                  label: 'Identity Verification',
                                  status: 'pending',
                                  description: 'Complete KYC verification with government-issued ID',
                                  actionUrl: request.acceptedBy === 'AMDAX' 
                                    ? 'https://amdax.com/kyc' 
                                    : 'https://empowermint.com/kyc',
                                  actionLabel: 'Start KYC Verification'
                                },
                                {
                                  id: 'iban',
                                  label: 'Bank Account Details',
                                  status: 'pending',
                                  description: 'Provide IBAN for fiat disbursement',
                                  actionUrl: request.acceptedBy === 'AMDAX' 
                                    ? 'https://amdax.com/iban' 
                                    : 'https://empowermint.com/iban',
                                  actionLabel: 'Submit IBAN'
                                },
                                {
                                  id: 'approval',
                                  label: 'Final Approval',
                                  status: 'pending',
                                  description: 'Bank reviews and approves your application (1-3 business days)'
                                }
                              ]}
                            />
                          </div>
                        )}
                      </div>
                      
                      {request.status === 'pending' && (
                        <div>
                          <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-s">Visible to partner banks:</p>
                          <div className="flex flex-wrap gap-3">
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => mockBankAcceptance(request.id, 'AMDAX')}
                              className="flex-1 sm:flex-none min-h-[44px] text-xs sm:text-sm"
                            >
                              [Demo] Amdax Accepts
                            </Button>
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => mockBankAcceptance(request.id, 'EMPOWERMINT')}
                              className="flex-1 sm:flex-none min-h-[44px] text-xs sm:text-sm"
                            >
                              [Demo] Empowermint Accepts
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Partner Banks */}
          <div>
            <h2 className="text-2xl font-bold mb-m">Partner Banks</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-l">
                  <div className="flex items-center justify-between mb-m">
                    <h3 className="text-xl font-bold">Amdax</h3>
                    <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-s">
                    European digital bank specializing in crypto-backed lending
                  </p>
                  <p className="font-['IBMPlexSans'] text-[12px] leading-normal text-text-secondary font-mono mb-m">
                    {BANK_PARTNERS.AMDAX}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-shades-extra-light rounded text-[12px] font-['Satoshi_Variable']">EUR</span>
                    <span className="px-2 py-1 bg-shades-extra-light rounded text-[12px] font-['Satoshi_Variable']">USD</span>
                    <span className="font-['Satoshi_Variable'] text-[12px] leading-normal text-text-secondary">• Revenue share: 50 bips</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-l">
                  <div className="flex items-center justify-between mb-m">
                    <h3 className="text-xl font-bold">Empowermint</h3>
                    <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-s">
                    DeFi-native lending platform with fiat on/off ramps
                  </p>
                  <p className="font-['IBMPlexSans'] text-[12px] leading-normal text-text-secondary font-mono mb-m">
                    {BANK_PARTNERS.EMPOWERMINT}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-shades-extra-light rounded text-[12px] font-['Satoshi_Variable']">EUR</span>
                    <span className="px-2 py-1 bg-shades-extra-light rounded text-[12px] font-['Satoshi_Variable']">GBP</span>
                    <span className="font-['Satoshi_Variable'] text-[12px] leading-normal text-text-secondary">• Revenue share: 50 bips</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ */}
          <FAQSection />
        </div>
      </div>

      {/* Loan Request Modal */}
      <Transition appear show={showLoanRequestModal} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-50" 
          onClose={() => !isSubmitting && setShowLoanRequestModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-lg transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="font-display font-black text-heading-lg uppercase text-text-primary leading-normal mb-4"
                  >
                    Request Loan
                  </Dialog.Title>
                  
                  <div className="space-y-6">
                    <InfoBox variant="info" title="How This Works">
                      <p className="text-sm">
                        Sign a message to create a public loan request. Partner banks can review and accept your request. No personal data is shared - only your wallet address, collateral, and requested LTV.
                      </p>
                    </InfoBox>

                    {/* Collateral Info */}
                    <div className="bg-shades-extra-light rounded-lg p-4">
                      <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-1">Your Collateral</p>
                      <p className="font-['IBMPlexSans'] text-[24px] font-bold leading-normal text-text-primary">${collateralValueUSD.toFixed(2)}</p>
                    </div>

                    {/* LOC Selector + Bank Partner Pre-fill (3e) */}
                    {locs.length > 0 && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Backed by LOC (optional)
                          </label>
                          <select
                            value={selectedLocEvent ? selectedLocEvent.id.toString() : ''}
                            onChange={(e) => {
                              const ev = locs.find(l => l.id.toString() === e.target.value)
                              setSelectedLocEvent(ev)
                            }}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange"
                          >
                            <option value="">— No LOC selected —</option>
                            {locs.map(loc => {
                              const isExpired = loc.expirationTimestamp < Math.floor(Date.now() / 1000)
                              if (isExpired) return null
                              return (
                                <option key={loc.id.toString()} value={loc.id.toString()}>
                                  LOC #{loc.id.toString()} — {loc.creditedTokenAmount.toString().slice(0, 6)} tokens
                                </option>
                              )
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Preferred Bank Partner
                          </label>
                          <select
                            value={selectedBankPartner}
                            onChange={(e) => setSelectedBankPartner(e.target.value as keyof typeof BANK_PARTNERS | '')}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange"
                          >
                            <option value="">— Any bank partner —</option>
                            {Object.entries(BANK_PARTNERS).map(([key, address]) => (
                              <option key={key} value={key}>{key} ({address.slice(0, 8)}...)</option>
                            ))}
                          </select>
                          {selectedBankPartner && (
                            <p className="mt-1 text-xs text-text-secondary font-mono">
                              Address: {BANK_PARTNERS[selectedBankPartner as keyof typeof BANK_PARTNERS]}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* LTV Slider */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm font-medium">Loan-to-Value (LTV) Ratio</label>
                        <span className={`text-sm font-bold ${getLtvColor(ltvSlider)}`}>
                          {ltvSlider}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max={MAX_LTV}
                        value={ltvSlider}
                        onChange={(e) => setLtvSlider(Number(e.target.value))}
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, 
                            #10b981 0%, 
                            #10b981 ${RECOMMENDED_LTV}%, 
                            #f59e0b ${RECOMMENDED_LTV}%, 
                            #f59e0b ${DANGER_LTV}%, 
                            #ef4444 ${DANGER_LTV}%, 
                            #ef4444 100%)`
                        }}
                        disabled={isSubmitting}
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Conservative</span>
                        <span className="text-green-600 font-semibold">{RECOMMENDED_LTV}% Recommended</span>
                        <span className="text-red-600">High Risk</span>
                      </div>
                    </div>

                    {/* Loan Amount */}
                    <div className="bg-gradient-to-r from-brand-orange/10 to-brand-blue/10 rounded-lg p-6 text-center">
                      <p className="font-['Satoshi_Variable'] text-[14px] leading-normal text-text-secondary mb-2">Loan Amount</p>
                      <p className="font-['IBMPlexSans'] text-[36px] font-bold leading-normal text-text-primary mb-1">
                        ${requestedLoanAmount.toFixed(2)}
                      </p>
                      {ltvSlider >= DANGER_LTV && (
                        <p className="text-sm text-red-600 font-semibold">
                          ⚠️ High LTV - Increased liquidation risk
                        </p>
                      )}
                      {ltvSlider === RECOMMENDED_LTV && (
                        <p className="text-sm text-green-600 font-semibold">
                          ✓ Optimal LTV - Low risk
                        </p>
                      )}
                    </div>

                    {/* Info about Revenue Share */}
                    <div className="font-['Satoshi_Variable'] text-[12px] leading-normal text-text-secondary bg-shades-extra-light p-3 rounded">
                      <p>
                        <strong>Revenue Model:</strong> Bitcoin.com receives 50 basis points (0.5%) from partner banks on funded loans. This ensures we only succeed when you get funded.
                      </p>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                      <Button 
                        variant="default" 
                        onClick={() => setShowLoanRequestModal(false)}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto min-h-[48px]"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSubmitLoanRequest}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className="w-full sm:flex-1 min-h-[48px]"
                      >
                        {isSubmitting ? 'Signing...' : 'Sign & Submit Request'}
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

// ── Loan Status Stepper — 4c ────────────────────────────────────────────────

const LOAN_STEPS: { key: string; label: string; description: string }[] = [
  { key: 'requested',    label: 'Requested',    description: 'Loan request submitted to partner banks' },
  { key: 'bank_review',  label: 'Bank Review',  description: 'Bank is reviewing your application' },
  { key: 'kyc',          label: 'KYC',          description: 'Complete identity verification' },
  { key: 'disbursement', label: 'Disbursement', description: 'Fiat funds being sent to your account' },
  { key: 'active',       label: 'Active',       description: 'Loan is active and in repayment' },
  { key: 'repaid',       label: 'Repaid',       description: 'Loan fully repaid, collateral released' },
]

// Map legacy statuses to stepper keys
function statusToStep(status: string): string {
  if (status === 'pending') return 'requested'
  if (status === 'accepted') return 'bank_review'
  if (status === 'completed') return 'repaid'
  return status
}

function LoanStatusStepper({
  status,
  className = '',
}: {
  status: string
  className?: string
}) {
  const stepKey = statusToStep(status)
  const currentIndex = LOAN_STEPS.findIndex(s => s.key === stepKey)
  const activeIndex = currentIndex >= 0 ? currentIndex : 0

  return (
    <div className={`${className}`}>
      <div className="flex items-center overflow-x-auto pb-2 gap-0">
        {LOAN_STEPS.map((step, idx) => {
          const isDone = idx < activeIndex
          const isActive = idx === activeIndex
          // isPending (idx > activeIndex) available if needed for styling
          return (
            <div key={step.key} className="flex items-center flex-shrink-0">
              {/* Step circle */}
              <div className={`flex flex-col items-center`}>
                <div
                  title={step.description}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                    isDone
                      ? 'bg-green-500 border-green-500 text-white'
                      : isActive
                      ? 'bg-brand-orange border-brand-orange text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                  }`}
                >
                  {isDone ? '✓' : idx + 1}
                </div>
                <span className={`mt-1 text-xs whitespace-nowrap font-medium hidden sm:block ${
                  isDone ? 'text-green-600' : isActive ? 'text-brand-orange' : 'text-gray-400'
                }`}>
                  {step.label}
                </span>
              </div>
              {/* Connector line */}
              {idx < LOAN_STEPS.length - 1 && (
                <div className={`h-0.5 w-8 sm:w-12 mx-1 ${idx < activeIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
          )
        })}
      </div>
      {/* Current step label on mobile */}
      <p className="sm:hidden text-xs text-text-secondary mt-1">
        Step {activeIndex + 1}: <span className="font-semibold text-brand-orange">{LOAN_STEPS[activeIndex]?.label}</span>
        {' — '}{LOAN_STEPS[activeIndex]?.description}
      </p>
    </div>
  )
}

// FAQ Section Component using Bitcoin.com React CL Accordion
function FAQSection() {
  const faqItems = [
    {
      id: '1',
      title: 'How do Bitcoin-backed loans work?',
      content: 'You deposit Bitcoin or other crypto assets into a self-custodial vault (smart contract). This collateral backs a loan request that partner banks can accept. You receive fiat currency (EUR, USD, GBP) while your crypto remains in your vault. The loan is overcollateralized to protect against price volatility. You can deposit various EVM-compatible assets including ETH, wBTC, cbBTC, USDC, USDT, and more.'
    },
    {
      id: '2',
      title: 'What is Loan-to-Value (LTV) ratio?',
      content: 'LTV is the percentage of your collateral value you can borrow. For example, with $10,000 in collateral at 50% LTV, you can borrow $5,000. We recommend staying at or below 50% LTV for safety. Higher LTVs (above 65%) carry increased liquidation risk if crypto prices drop.'
    },
    {
      id: '3',
      title: 'Do I maintain custody of my Bitcoin?',
      content: 'Yes! Your Bitcoin stays in your self-custodial vault (smart contract) that only you control. Unlike centralized lending platforms, there is no rehypothecation, no lending out your assets, and no counterparty risk. You can withdraw your collateral anytime after repaying the loan.'
    },
    {
      id: '4',
      title: 'What is an LOC (Letter of Credit)?',
      content: 'A Letter of Credit is an on-chain payment guarantee backed by your vault collateral. When you request a loan, you issue an LOC to the bank partner. The LOC ensures the bank can claim collateral if loan terms are not met, while giving you access to fiat liquidity.'
    },
    {
      id: '5',
      title: 'How does Bitcoin.com make money?',
      content: 'Bitcoin.com receives 50 basis points (0.5%) from partner banks on successfully funded loans. This aligns our incentives - we only earn when you get funded. There are no upfront fees or hidden charges to users.'
    },
    {
      id: '6',
      title: 'Which banks can accept my loan request?',
      content: 'Currently, we partner with Amdax and Empowermint. Your loan request is visible to all partner banks who can independently review and accept it based on their lending criteria. Each bank operates from a verified on-chain address.'
    },
    {
      id: '7',
      title: 'What happens after a bank accepts my request?',
      content: 'Once a bank accepts your loan request, you\'ll be redirected to complete KYC (Know Your Customer) verification on their platform and provide IBAN details for fiat disbursement. This is required by financial regulations. After KYC approval, funds are transferred to your bank account.'
    },
    {
      id: '8',
      title: 'What are the tax implications?',
      content: 'Borrowing against your Bitcoin is generally not a taxable event (consult your tax advisor). This is more tax-efficient than selling Bitcoin, which triggers capital gains taxes. You maintain exposure to Bitcoin price appreciation while accessing liquidity.'
    },
    {
      id: '9',
      title: 'What is the Anvil Protocol?',
      content: 'Anvil is the underlying DeFi protocol that powers the vaults and LOC system. It provides secure, audited smart contracts for collateral management and trustless credit issuance. All transactions are transparent and verifiable on-chain.'
    },
    {
      id: '10',
      title: 'What happens if Bitcoin price drops?',
      content: 'If your collateral value drops significantly, you may face liquidation to protect the lender. This is why we recommend staying at 50% LTV or lower. You can always add more collateral to your vault to maintain a healthy LTV ratio and avoid liquidation.'
    },
    {
      id: '11',
      title: 'Can I deposit yield-generating assets like wstETH?',
      content: 'Yes! You can deposit yield-generating assets like wstETH (Wrapped Staked ETH from Lido) or other liquid staking tokens. This allows you to earn staking yields on Ethereum while using that same ETH as collateral for your loan. Your wstETH continues to accrue staking rewards in your vault while backing your loan, giving you dual benefits: fiat liquidity AND ETH staking yield.'
    },
    {
      id: '12',
      title: 'What crypto assets can I use as collateral?',
      content: 'You can deposit any EVM-compatible assets including: Ethereum (ETH), Wrapped Bitcoin (wBTC), Coinbase Wrapped Bitcoin (cbBTC), stablecoins (USDC, USDT, DAI), liquid staking tokens (wstETH, rETH), and many other ERC-20 tokens. Each asset is valued in real-time using Chainlink price oracles to calculate your total collateral value and available borrowing capacity.'
    },
    {
      id: '13',
      title: 'How do I use native Bitcoin (BTC) as collateral?',
      content: 'Native Bitcoin (BTC) needs to be wrapped into an EVM-compatible token like wBTC (Wrapped Bitcoin) or cbBTC (Coinbase Wrapped Bitcoin) to be used in the smart contract vault. We offer a seamless one-click swap and deposit flow that automatically converts your BTC to wBTC/cbBTC and deposits it into your vault in a single transaction. This makes the process simple while maintaining the value of your Bitcoin as collateral.'
    },
  ]

  return (
    <div className="space-y-l">
      <h2 className="font-display font-black text-heading-xl uppercase text-text-primary text-center">
        Frequently Asked Questions
      </h2>
      <div className="max-w-3xl mx-auto">
        <Accordion items={faqItems} allowMultiple={true} allowToggle={true} />
      </div>
    </div>
  )
}
